/**
 * `useAccountsQuery` - TanStack Query wrapper around `BffClient.listAccounts`.
 *
 * Pagination model: cursor-based, exposed as `useInfiniteQuery`. The first
 * page is fetched on mount; the user's `fetchNextPage()` call follows
 * `lastPage.next_cursor`. `getNextPageParam` returns `undefined` at end
 * of list, which disables the "Next" control.
 *
 * The query key encodes the *effective* filter set (not the raw URL),
 * so changing the limit or applying a filter invalidates the cache
 * appropriately. The cursor is intentionally NOT in the key - the cursor
 * is the page param, not a separate query.
 *
 * Status filter is hardwired to `active,suspended` at M1 (the FR-2
 * default; closed/deleted require an explicit opt-in that lands in M2).
 */

import { useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query'

import type { AccountListPage, BffClient, ListAccountsParams } from '../../lib/bff-client'
import type { AccountsUrlState } from './url-state'

/** Default status filter applied on every M1 list call. */
export const DEFAULT_STATUS = 'active,suspended'

export interface UseAccountsQueryArgs {
  readonly client: BffClient
  readonly state: AccountsUrlState
  readonly onError?: (err: unknown) => void
}

export type AccountsInfiniteResult = UseInfiniteQueryResult<
  { pages: AccountListPage[]; pageParams: (string | undefined)[] },
  Error
>

export function accountsQueryKey(state: AccountsUrlState): readonly unknown[] {
  return [
    'accounts',
    {
      extAccountId: state.extAccountId,
      legacyId: state.legacyId,
      limit: state.limit,
      status: DEFAULT_STATUS,
    },
  ] as const
}

export function useAccountsQuery({
  client,
  state,
  onError,
}: UseAccountsQueryArgs): AccountsInfiniteResult {
  return useInfiniteQuery({
    queryKey: accountsQueryKey(state),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      // Build the params object additively so optional fields are
      // only attached when set (exactOptionalPropertyTypes is on).
      const params: ListAccountsParams = {
        status: DEFAULT_STATUS,
        limit: state.limit,
      }
      const writable = params as {
        -readonly [K in keyof ListAccountsParams]: ListAccountsParams[K]
      }
      if (state.extAccountId !== null) writable.ext_account_id = state.extAccountId
      if (state.legacyId !== null) writable.legacy_id = state.legacyId
      if (pageParam !== undefined) writable.cursor = pageParam
      return await client.listAccounts(params)
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    // The list page is read-only and short-lived per session; cache for
    // 30s to coalesce rapid filter-tweak round-trips but never serve
    // truly stale data. Aligned with main.tsx defaults.
    staleTime: 30_000,
    retry: (failureCount, err) => {
      // Do not retry auth / authz errors - they need user action, not
      // another network round-trip.
      const status = (err as { status?: number }).status
      if (status === 401 || status === 403 || status === 400) {
        return false
      }
      return failureCount < 1
    },
    // Surface errors to the shared handler so 401/403 routing happens
    // even if the page-level UI does not need to render anything new.
    throwOnError: false,
    meta: { onError },
  })
}
